import React from "react";
import {
  Type,
  Image as ImageIcon,
  MousePointer,
  Box,
  Menu,
  Grid3X3,
  Columns,
  Plus,
} from "lucide-react";

// Types for component structure
export type ComponentVariant = {
  name: string;
  preview: () => React.JSX.Element;
  description: string;
};

export type ComponentCategory = {
  icon: React.ComponentType<{ className?: string }>;
  variants: ComponentVariant[];
};

export type ComponentCollection = {
  [key: string]: ComponentCategory;
};

// Component categories data with preview components
export const globalComponents: ComponentCollection = {
  Text: {
    icon: Type,
    variants: [
      {
        name: "Heading",
        preview: () => (
          <div className="text-lg font-bold text-foreground">
            Sample Heading
          </div>
        ),
        description: "Large display text for titles",
      },
      {
        name: "Paragraph",
        preview: () => (
          <div className="text-sm text-muted-foreground">
            This is a sample paragraph text that shows how content will appear.
          </div>
        ),
        description: "Body text for content",
      },
      {
        name: "Quote",
        preview: () => (
          <div className="border-l-2 border-primary pl-3">
            <div className="text-sm italic text-muted-foreground">
              &ldquo;This is a sample quote&rdquo;
            </div>
          </div>
        ),
        description: "Stylized quotation block",
      },
      {
        name: "List",
        preview: () => (
          <div className="space-y-1 text-xs">
            <div className="flex items-center space-x-2">
              <div className="w-1 h-1 bg-foreground rounded-full"></div>
              <span>First item</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-1 h-1 bg-foreground rounded-full"></div>
              <span>Second item</span>
            </div>
          </div>
        ),
        description: "Bulleted or numbered lists",
      },
    ],
  },
  Media: {
    icon: ImageIcon,
    variants: [
      {
        name: "Image",
        preview: () => (
          <div className="w-full h-8 bg-gradient-to-r from-muted to-muted-foreground/20 rounded flex items-center justify-center">
            <ImageIcon className="w-4 h-4 text-muted-foreground" />
          </div>
        ),
        description: "Single image display",
      },
      {
        name: "Gallery",
        preview: () => (
          <div className="grid grid-cols-2 gap-1">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-square bg-muted rounded"></div>
            ))}
          </div>
        ),
        description: "Image gallery grid",
      },
      {
        name: "Video",
        preview: () => (
          <div className="w-full h-8 bg-muted rounded flex items-center justify-center relative">
            <div className="w-3 h-3 bg-primary rounded-full flex items-center justify-center">
              <div className="w-0 h-0 border-l-[4px] border-l-white border-y-[2px] border-y-transparent ml-0.5"></div>
            </div>
          </div>
        ),
        description: "Video player component",
      },
      {
        name: "Icon",
        preview: () => (
          <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
            <div className="w-3 h-3 bg-white rounded-full"></div>
          </div>
        ),
        description: "Icon display element",
      },
    ],
  },
  Forms: {
    icon: MousePointer,
    variants: [
      {
        name: "Button",
        preview: () => (
          <div className="bg-primary text-primary-foreground px-3 py-1 rounded text-xs font-medium">
            Click Me
          </div>
        ),
        description: "Interactive button element",
      },
      {
        name: "Input",
        preview: () => (
          <div className="border border-border dark:border-auth-background rounded px-2 py-1 text-xs bg-background">
            Enter text...
          </div>
        ),
        description: "Text input field",
      },
      {
        name: "Textarea",
        preview: () => (
          <div className="border border-border dark:border-auth-background rounded px-2 py-2 text-xs bg-background h-8">
            Multi-line text...
          </div>
        ),
        description: "Multi-line text input",
      },
      {
        name: "Checkbox",
        preview: () => (
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 border border-border dark:border-auth-background rounded bg-primary flex items-center justify-center">
              <div className="w-1 h-1 bg-white rounded-sm"></div>
            </div>
            <span className="text-xs">Option</span>
          </div>
        ),
        description: "Checkbox selection",
      },
      {
        name: "Radio",
        preview: () => (
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 border border-border dark:border-auth-background rounded-full bg-primary flex items-center justify-center">
              <div className="w-1 h-1 bg-white rounded-full"></div>
            </div>
            <span className="text-xs">Choice</span>
          </div>
        ),
        description: "Radio button selection",
      },
      {
        name: "Select",
        preview: () => (
          <div className="border border-border dark:border-auth-background rounded px-2 py-1 text-xs bg-background flex items-center justify-between">
            <span>Select option</span>
            <div className="w-2 h-2 border-r border-b border-border dark:border-auth-background transform rotate-45"></div>
          </div>
        ),
        description: "Dropdown selection",
      },
    ],
  },
  Layout: {
    icon: Box,
    variants: [
      {
        name: "Container",
        preview: () => (
          <div className="border-2 border-dashed border-muted-foreground/30 rounded p-2 text-xs text-center text-muted-foreground">
            Container
          </div>
        ),
        description: "Content wrapper container",
      },
      {
        name: "Section",
        preview: () => (
          <div className="bg-muted/50 rounded p-2 text-xs text-center">
            Section Block
          </div>
        ),
        description: "Page section divider",
      },
      {
        name: "Divider",
        preview: () => <div className="w-full h-px bg-border my-2"></div>,
        description: "Visual separator line",
      },
      {
        name: "Spacer",
        preview: () => (
          <div className="w-full h-4 bg-gradient-to-r from-transparent via-muted-foreground/10 to-transparent"></div>
        ),
        description: "Empty spacing element",
      },
    ],
  },
  Navigation: {
    icon: Menu,
    variants: [
      {
        name: "Menu",
        preview: () => (
          <div className="flex space-x-2 text-xs">
            <span className="text-primary">Home</span>
            <span className="text-muted-foreground">About</span>
            <span className="text-muted-foreground">Contact</span>
          </div>
        ),
        description: "Navigation menu",
      },
      {
        name: "Breadcrumb",
        preview: () => (
          <div className="flex items-center space-x-1 text-xs">
            <span>Home</span>
            <span className="text-muted-foreground">›</span>
            <span>Products</span>
          </div>
        ),
        description: "Breadcrumb navigation",
      },
      {
        name: "Pagination",
        preview: () => (
          <div className="flex items-center space-x-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-4 h-4 rounded text-xs flex items-center justify-center ${i === 2 ? "bg-primary text-primary-foreground" : "bg-muted"}`}
              >
                {i}
              </div>
            ))}
          </div>
        ),
        description: "Page navigation controls",
      },
      {
        name: "Tabs",
        preview: () => (
          <div className="flex text-xs">
            <div className="px-2 py-1 bg-primary text-primary-foreground rounded-l">
              Tab 1
            </div>
            <div className="px-2 py-1 bg-muted text-muted-foreground">
              Tab 2
            </div>
            <div className="px-2 py-1 bg-muted text-muted-foreground rounded-r">
              Tab 3
            </div>
          </div>
        ),
        description: "Tabbed content navigation",
      },
    ],
  },
  Content: {
    icon: Grid3X3,
    variants: [
      {
        name: "Card",
        preview: () => (
          <div className="border border-border dark:border-auth-background rounded p-2 bg-background">
            <div className="text-xs font-medium mb-1">Card Title</div>
            <div className="text-xs text-muted-foreground">
              Card content goes here...
            </div>
          </div>
        ),
        description: "Content card container",
      },
      {
        name: "Table",
        preview: () => (
          <div className="border border-border dark:border-auth-background rounded">
            <div className="grid grid-cols-2 gap-px bg-border">
              <div className="bg-muted p-1 text-xs font-medium">Header</div>
              <div className="bg-muted p-1 text-xs font-medium">Value</div>
              <div className="bg-background p-1 text-xs">Row 1</div>
              <div className="bg-background p-1 text-xs">Data</div>
            </div>
          </div>
        ),
        description: "Data table display",
      },
      {
        name: "Accordion",
        preview: () => (
          <div className="border border-border dark:border-auth-background rounded">
            <div className="p-2 border-b border-border dark:border-auth-background bg-muted text-xs font-medium">
              Section 1 ▼
            </div>
            <div className="p-2 text-xs text-muted-foreground">
              Content here...
            </div>
          </div>
        ),
        description: "Collapsible content sections",
      },
      {
        name: "Carousel",
        preview: () => (
          <div className="relative border border-border dark:border-auth-background rounded p-2">
            <div className="text-xs text-center">Slide Content</div>
            <div className="flex justify-center space-x-1 mt-1">
              <div className="w-1 h-1 bg-primary rounded-full"></div>
              <div className="w-1 h-1 bg-muted rounded-full"></div>
              <div className="w-1 h-1 bg-muted rounded-full"></div>
            </div>
          </div>
        ),
        description: "Image/content carousel",
      },
    ],
  },
};

export const customComponents: ComponentCollection = {
  "My Templates": {
    icon: Columns,
    variants: [
      {
        name: "Header Template",
        preview: () => (
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded p-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-medium">My Store</div>
              <div className="flex space-x-1">
                <div className="w-1 h-1 bg-primary rounded-full"></div>
                <div className="w-1 h-1 bg-primary rounded-full"></div>
                <div className="w-1 h-1 bg-primary rounded-full"></div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Custom header layout
            </div>
          </div>
        ),
        description: "Pre-designed header section",
      },
      {
        name: "Footer Template",
        preview: () => (
          <div className="bg-muted/50 border border-border dark:border-auth-background rounded p-2">
            <div className="grid grid-cols-3 gap-1 mb-1">
              <div className="text-xs">Links</div>
              <div className="text-xs">Contact</div>
              <div className="text-xs">Social</div>
            </div>
            <div className="text-xs text-muted-foreground text-center">
              © 2025 My Store
            </div>
          </div>
        ),
        description: "Pre-designed footer section",
      },
      {
        name: "Hero Section",
        preview: () => (
          <div className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded p-2">
            <div className="text-xs font-bold mb-1">Welcome to Our Store</div>
            <div className="text-xs text-muted-foreground mb-1">
              Discover amazing products
            </div>
            <div className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs inline-block">
              Shop Now
            </div>
          </div>
        ),
        description: "Hero banner with call-to-action",
      },
    ],
  },
  "Saved Blocks": {
    icon: Plus,
    variants: [
      {
        name: "Product Grid",
        preview: () => (
          <div className="grid grid-cols-2 gap-1">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="border border-border dark:border-auth-background rounded p-1"
              >
                <div className="w-full h-4 bg-muted rounded mb-1"></div>
                <div className="text-xs">Product {i}</div>
                <div className="text-xs font-medium">$99</div>
              </div>
            ))}
          </div>
        ),
        description: "Product showcase grid",
      },
      {
        name: "Testimonial Block",
        preview: () => (
          <div className="bg-background border border-border dark:border-auth-background rounded p-2">
            <div className="text-xs italic mb-1">
              &ldquo;Great experience with this store!&rdquo;
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-muted rounded-full"></div>
              <div className="text-xs font-medium">John Doe</div>
            </div>
          </div>
        ),
        description: "Customer testimonial display",
      },
      {
        name: "Contact Form",
        preview: () => (
          <div className="space-y-1">
            <div className="border border-border dark:border-auth-background rounded px-1 py-0.5 text-xs bg-background">
              Name
            </div>
            <div className="border border-border dark:border-auth-background rounded px-1 py-0.5 text-xs bg-background">
              Email
            </div>
            <div className="border border-border dark:border-auth-background rounded px-1 py-1 text-xs bg-background h-6">
              Message
            </div>
            <div className="bg-primary text-primary-foreground px-2 py-0.5 rounded text-xs">
              Send
            </div>
          </div>
        ),
        description: "Contact form with validation",
      },
    ],
  },
};
